import React, { useState } from "react";
import {
  Button,
  DateField,
  DropdownField,
  Modal,
  TextAreaField,
} from "../../../components/ui";
import {
  DURATION_OPTIONS,
  TIME_OPTIONS,
  clashWith,
  durationOf,
  timeOptionsWithBookings,
  isWhenComplete,
  nextSlotAfter,
  startOfDay,
  startsAtOf,
  whenErrors,
  type AppointmentWhen,
} from "./appointmentFields";
import { useDayAppointments } from "../hooks/useDayAppointments";
import type {
  AppointmentAttendee,
  AppointmentSubject,
  AppointmentType,
  NewClientAppointment,
} from "../types/appointments.types";
import { useMeetingApps } from "../hooks/useMeetingApps";
import { AGENDA_OPTIONS, MAX_DESCRIPTION } from "../meetingAgenda";
// Deep imports rather than the brand barrel, which pulls in the whole My
// brand screen and its dialogs; this needs the saved details and the one line
// that reads an address out of them.
import { useBrandDetails } from "../../brand/hooks/useBrandDetails";
import { brandAddressLine } from "../../brand/brandAddress";
import AppointmentAddressNote from "./AppointmentAddressNote";
import ConnectAppPrompt from "./ConnectAppPrompt";
import MeetingAppMark from "./MeetingAppMark";
// Deep import rather than the clients barrel, which pulls in every client
// screen; this wants one field. The picker mounts `useClients` with itself, so
// a dialog opened without `pickClient` never asks for the roster at all.
import ClientPickerField from "../../clients/components/ClientPickerField";
// And the enquiry-side twin of it, deep-imported for the same reason: the
// enquiries barrel carries four screens behind one field.
import EnquiryPickerField from "../../enquiries/components/EnquiryPickerField";
// The two glyphs the design puts on the Client and Enquiry buttons are the
// rail's own — the same mark each of those screens is known by, so the button
// says which list is about to be searched before the list is opened.
import { ReactComponent as ClientMark } from "../../../assets/svg/sidebar/clients-active.svg";
import { ReactComponent as EnquiryMark } from "../../../assets/svg/sidebar/Enquiries.svg";

/** Both answers to "where does this happen", in the order the design draws them. */
const TYPES: AppointmentType[] = ["Online", "Offline"];

/**
 * Both answers to "who is this with", in the order the design draws them, and
 * what each button says.
 *
 * "Enquiry" rather than the backend's "inquiry": the wire's spelling stops at
 * the service, and every screen in this app has always said enquiry. See
 * `AppointmentSubject`.
 */
const SUBJECTS: {
  value: AppointmentSubject;
  label: string;
  Mark: React.FC<React.SVGProps<SVGSVGElement>>;
}[] = [
  { value: "client", label: "Client", Mark: ClientMark },
  { value: "inquiry", label: "Enquiry", Mark: EnquiryMark },
];

export interface ScheduleAppointmentModalProps {
  /**
   * Whether the dialog has to ask who the appointment is with.
   *
   * Off on a client's own screen, which already knows — the dialog is opened
   * from their record and the booking goes against it. On from My task, where
   * the screen is a day rather than a person and who it is with is the first
   * thing the booking is missing. One dialog with a field that appears rather
   * than two dialogs asking the same six questions: the day and the time and
   * the length are the same questions wherever they are asked, and the clash
   * checking under them is the part that would quietly drift apart.
   *
   * Still named for the client because that is what it asks first; what it
   * actually turns on is the whole "Select user" step, which is a choice
   * between two rosters and then a name out of one of them.
   */
  pickClient?: boolean;
  /** What the panel calls itself, where the caller has a better name for it. */
  title?: string;
  /** Set while the booking is with the server. */
  busy?: boolean;
  onClose: () => void;
  /**
   * @param attendee whose it is, and which of the two kinds they are — only
   * ever set when `pickClient` is on. A caller that already knows the client
   * ignores it.
   */
  onConfirm: (input: NewClientAppointment, attendee?: AppointmentAttendee) => void;
}

/**
 * Books an appointment: where it happens, when, for how long, and, for one
 * that happens somewhere, the address to come to — and, where the screen that
 * opened it does not already know, who it is with.
 *
 * That last question is `pickClient`, and it is the only difference between
 * the dialog a client's own screen opens and the one My task opens. Everything
 * under it is the same six controls checking the same diary for the same
 * clashes, which is exactly why it is a field that appears rather than a
 * second dialog.
 *
 * It is two questions, not one: which roster, and then which name out of it.
 * An hour can be booked with a client or with somebody who has only enquired
 * so far — a first consultation is the obvious one, and until now it could
 * not be booked at all without first making a client out of a lead who may
 * never become one. The two are different records against different
 * endpoints, so the button says which is being searched before the list
 * opens, and changing it clears the name: an enquiry's id sent as a client's
 * is a booking against somebody who does not exist. See `AppointmentAttendee`.
 *
 * An online one asks for no link. The link is the connected app's to make —
 * see `useMeetingApps` — so while an app is connected there is nothing under
 * the duration at all, and while none is, the space says what would make one
 * rather than asking for a URL to be pasted into it.
 *
 * The agenda and the description are both optional, and neither holds the
 * Schedule button closed. A practice books a slot in somebody's week far more
 * often than it writes an essay about one, and a dialog that refused to book
 * an appointment until a paragraph had been typed would be one where the
 * paragraph is "." — which is worse than nothing, because nothing can be
 * drawn as nothing. The agenda is a closed list of seven rather than a box,
 * for the reason given on `MEETING_AGENDAS`.
 *
 * No answer is pre-filled. A date defaulted to today and a time defaulted to
 * the next quarter hour would both be guesses, and a dialog that opens already
 * answered is one whose Schedule button can be pressed before anybody has read
 * the question. The button is held closed until there is a real moment and a
 * real length.
 *
 * The address is not asked at all: an in-person appointment happens at the
 * practice, and the practice's address is already on record — see
 * `brandAddressLine`. So it is shown rather than requested, marked as the
 * default it is, and changed where it is kept rather than retyped into every
 * appointment. A practice that has written none down yet is told where to,
 * which is the one thing a box here could never have said.
 *
 * The moment has to be ahead. Days behind today cannot be picked and are
 * refused if typed — see `whenErrors`, which is also where the note lives on
 * what that costs.
 */
const ScheduleAppointmentModal: React.FC<ScheduleAppointmentModalProps> = ({
  pickClient = false,
  title = "Schedule appointment",
  busy = false,
  onClose,
  onConfirm,
}) => {
  // Who it is with, in two parts: which roster, and which row of it. Two
  // pieces of state rather than one `AppointmentAttendee`, because the id has
  // to be cleared when the roster changes and a single value would make
  // "client, not chosen yet" and "nobody" the same thing.
  const [subject, setSubject] = useState<AppointmentSubject>("client");
  const [attendeeId, setAttendeeId] = useState("");
  const [type, setType] = useState<AppointmentType>("Online");
  // What the appointment is for, and what it is about. Both optional — see the
  // note on the panel above — so neither is part of `ready`.
  const [agenda, setAgenda] = useState("");
  const [description, setDescription] = useState("");
  const [when, setWhen] = useState<AppointmentWhen>({
    date: "",
    time: "",
    duration: "",
  });
  // Where the practice sees people. Read from the brand it has already saved,
  // and shared with the My brand screen's own cache, so a dialog opened after
  // that screen has been visited shows the address without waiting on
  // anything. "" while it is still in flight, and for a practice that has
  // written none.
  const { loaded: brand } = useBrandDetails();
  const practiceAddress = brand ? brandAddressLine(brand) : "";

  // Only ever asked one thing: whether a link can be made at all. Which app
  // makes it is the Integrate apps card's business, not this dialog's.
  const { anyConnected, activeApp } = useMeetingApps();

  // What the chosen day already holds, across every client: the practitioner
  // cannot be in two places, so an hour promised to somebody else is not free
  // because this dialog is open on this client.
  const day = useDayAppointments(when.date);
  // Reported by closing the hours off on the list rather than by arguing with
  // a time already chosen — see `timeOptionsWithBookings`. The check below is
  // kept as the guard on the button, for the one way a taken slot can still
  // be held: picking a free time and then lengthening it over the next
  // booking, which the list answers by clearing the time.
  const clash = clashWith(when, day.booked);
  const times = timeOptionsWithBookings(
    TIME_OPTIONS,
    when.date,
    durationOf(when),
    day.booked
  );

  // Read once, when the dialog opens, rather than on every render: a "now"
  // that moved underneath the fields would let a slot chosen a moment ago
  // start failing while somebody is still filling in the address.
  const [now] = useState(() => new Date());

  const set = (patch: Partial<AppointmentWhen>) =>
    setWhen((current) => ({ ...current, ...patch }));

  const errors = whenErrors(when, now);
  const ready =
    // Nobody to book it with is the one unanswered question that is not about
    // the slot, so it is checked first and separately. Where the dialog is not
    // asking, there is nothing to check.
    (!pickClient || !!attendeeId) &&
    isWhenComplete(when) &&
    !errors.date &&
    !errors.time &&
    // A clash closes the button; a check still in flight does not. The day is
    // read the moment a date is picked, long before a time and a length have
    // been chosen, so the answer is there by the time anybody reaches the
    // button — and a button that greyed out for every read would blink on a
    // slow connection with nothing to say for itself. A check that could not
    // be made is not a clash either: refusing to book because the server was
    // unreachable buys no safety, and the endpoint has the last word anyway.
    !clash;

  const handleConfirm = () => {
    const startsAt = startsAtOf(when);
    if (!startsAt || busy) return;
    if (pickClient && !attendeeId) return;

    onConfirm(
      {
        startsAt,
        type,
        durationMinutes: durationOf(when),
        // `null` rather than `""` where they were left alone, so the service
        // has one thing to test and leaves the field off the payload.
        agenda: agenda || null,
        description: description.trim() || null,
        // Never set here any more: an online appointment's link comes from the
        // connected app, and this dialog has no box to type one into. Sent as
        // `null` rather than left out so the payload keeps the shape the
        // service and the endpoint already agree on.
        meetingUrl: null,
        // The practice's own, since that is where an in-person appointment
        // happens and there is nowhere here to say otherwise.
        address: type === "Offline" ? practiceAddress || null : null,
      },
      // Left off entirely where the dialog did not ask, rather than sent as
      // "": a caller that already knows the client should not have to work out
      // that an empty string means "the one you already had".
      pickClient ? { subject, id: attendeeId } : undefined
    );
  };

  return (
    <Modal
      open
      title={title}
      // Not while it is with the server: the appointment is being booked
      // either way, and a dialog closed over it would leave nothing to say so.
      onClose={() => !busy && onClose()}
      className="max-w-[824px]"
      footer={
        <>
          <Button variant="secondary" size="md" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="md"
            loading={busy}
            disabled={!ready}
            onClick={handleConfirm}
          >
            Schedule
          </Button>
        </>
      }
    >
      {/* Two columns, as the design draws it: what the appointment is
          about on the left, and when and where it happens on the right.
          The split is not decoration — the description is a paragraph,
          and a paragraph box stacked under six short controls either
          squashes to four lines or pushes the Schedule button off a
          laptop screen. One column below `sm`, where there is no width to
          split and the dialog is read top to bottom anyway. */}
      <div className="flex flex-col gap-5 sm:flex-row sm:gap-0">
        <div className="flex min-w-0 flex-1 flex-col gap-4 sm:pr-6">
          {/* First, because it is the first thing anybody filling this in from a
              day's diary has to decide — and because every question under it is
              about a slot in that person's week. */}
          {pickClient && (
            <>
              <fieldset>
                {/* A `<legend>`, as on Appointment type across the way: the
                    two boxes under it are one question with two answers,
                    which is what a fieldset says to anything reading the
                    dialog out. */}
                <legend className="block text-[13px] 3xl:text-[14px] text-ink mb-1.5">
                  Select user
                </legend>
                {/* Each button at its own width, rather than the halved grid
                    Appointment type uses across the way. The two questions
                    look alike but are not the same shape: that one is a
                    choice between two states of the appointment itself and
                    is drawn as the width of the thing it describes, and this
                    one is a switch over the field beneath it — so it sits at
                    the size of its two words and leaves the column to the
                    name box, which is what the row is actually for. */}
                <div className="flex flex-wrap gap-3">
                  {SUBJECTS.map(({ value, label, Mark }) => {
                    const active = value === subject;

                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={active}
                        disabled={busy}
                        // The name goes with the roster. An enquiry's id is
                        // not a client's and would be sent under the wrong
                        // key, so switching clears what was picked rather
                        // than carrying it across — and the field below says
                        // so by going back to its own placeholder.
                        onClick={() => {
                          setSubject(value);
                          setAttendeeId("");
                        }}
                        className={`flex items-center justify-center gap-2 rounded-[6px] border px-4 py-2.5 text-[14px] transition-colors disabled:cursor-not-allowed ${
                          active
                            ? "border-[#00C2CB] bg-[#E6FAFB] text-ink font-medium"
                            : "border-line bg-white text-ink hover:border-[#1E1F24]"
                        }`}
                      >
                        <Mark aria-hidden="true" className="h-[18px] w-[18px]" />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              {/* One field in two forms, and never both at once. Each mounts
                  its own list — see either picker — so the roster that is not
                  being searched is never fetched. The label is kept for
                  anything reading the dialog out and taken off the screen:
                  the legend above has just asked this question, and a second
                  label under it would ask it twice. */}
              {subject === "client" ? (
                <ClientPickerField
                  id="appointment-client"
                  label="Client name"
                  hideLabel
                  clientId={attendeeId}
                  onChange={(picked) => setAttendeeId(picked)}
                />
              ) : (
                <EnquiryPickerField
                  id="appointment-enquiry"
                  label="Enquiry name"
                  hideLabel
                  enquiryId={attendeeId}
                  onChange={(picked) => setAttendeeId(picked)}
                />
              )}
            </>
          )}
          <DropdownField
            label="Select agenda"
            value={agenda}
            placeholder="Agenda"
            options={AGENDA_OPTIONS}
            // Seven rows, which is the whole list: a dropdown that stopped at
            // five and waited to be scrolled would hide two of the seven things
            // an appointment can be.
            optionPageSize={AGENDA_OPTIONS.length}
            onValueChange={setAgenda}
            disabled={busy}
          />

          {/* The reason the dialog has two columns at all: it is the one
              answer here that is written rather than picked, and a four-line
              box under six dropdowns reads as an afterthought.

              `grow` is what squares the two columns off against each other.
              The box takes whatever height the right-hand side has that this
              one has not used, so the two sides end on the same line instead
              of the shorter one stopping halfway down and leaving a block of
              air beside the other. It is also why `rows` is left at the floor
              rather than set tall: the height is worked out from the column,
              and the number is only what the box falls back to — when the two
              columns stack on a narrow screen, and when this side is the
              taller of the two anyway, which it is once the client picker is
              on it.

              The count beside it is the limit said out loud — the field
              refuses the 501st character, so nobody meets the ceiling as a
              rejected booking. */}
          <TextAreaField
            grow
            label="Description"
            value={description}
            rows={4}
            placeholder="Detail of meeting"
            maxLength={MAX_DESCRIPTION}
            hint={`${description.length}/${MAX_DESCRIPTION}`}
            onValueChange={setDescription}
            disabled={busy}
          />
        </div>

        {/* The rule between the columns belongs to this one, so it runs
            the height of whichever column is taller rather than stopping
            where the shorter one ends. */}
        <div className="flex min-w-0 flex-1 flex-col gap-4 sm:border-l sm:border-line sm:pl-6">
          <fieldset>
            {/* A `<legend>` rather than a floating label: the two boxes under it
                are one question with two answers, which is what a fieldset says
                to anything reading the dialog out. */}
            <legend className="block text-[13px] 3xl:text-[14px] text-ink mb-1.5">
              Appointment type
            </legend>
            <div className="grid grid-cols-2 gap-4">
              {TYPES.map((option) => {
                const active = option === type;

                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={active}
                    disabled={busy}
                    // Nothing to carry across: Online asks for no address, and
                    // Offline asks for none either — it shows the practice's.
                    onClick={() => setType(option)}
                    className={`flex items-center justify-center gap-2 rounded-[6px] border px-4 py-2.5 text-[14px] transition-colors disabled:cursor-not-allowed ${
                      active
                        ? "border-[#00C2CB] bg-[#E6FAFB] text-ink font-medium"
                        : "border-line bg-white text-ink hover:border-[#1E1F24]"
                    }`}
                  >
                    {/* Whose call it will be, on the button that decides there
                        is one. The mark stands for the app that is connected —
                        it changes with the setting — so choosing Online says
                        where the client is being sent rather than leaving that
                        to be discovered when the invitation lands. Nothing is
                        drawn while no app is connected: there is no link to
                        promise, and the strip below says so. */}
                    {option === "Online" && activeApp && (
                      <MeetingAppMark app={activeApp} size={18} />
                    )}
                    {option}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* The whole width: a date is the one answer here somebody types
              rather than picks, and it shares the row with nothing. */}
          <DateField
            label="Date"
            value={when.date}
            onValueChange={(value) => set({ date: value })}
            placeholder="DD/MM/YY"
            // Today onwards. The calendar greys out what is behind us; the same
            // rule is checked again on the typed value, which no `minDate` can
            // reach.
            minDate={startOfDay(now)}
            error={errors.date}
            disabled={busy}
          />

          <div className="grid grid-cols-2 gap-4">
            <DropdownField
              label="Time"
              value={when.time}
              placeholder="11:00 AM"
              // The day's own list: every hour already promised to somebody is
              // struck through, says whose it is, and cannot be picked.
              options={times}
              // The whole day at once: the list is read by scrolling down it to
              // an hour, and one that stopped part-way through the afternoon
              // until it was nudged would read as a day that ends at three.
              optionPageSize={TIME_OPTIONS.length}
              // Opened at the next quarter hour rather than at midnight — it
              // scrolls the list and answers nothing. See `nextSlotAfter`.
              openAt={nextSlotAfter(now)}
              onValueChange={(value) => set({ time: value })}
              // Only ever set on a slot chosen for today: the whole day is
              // offered, and an hour that has gone is refused rather than
              // hidden, so the list reads the same on every day.
              error={errors.time}
              disabled={busy}
            />
            <DropdownField
              label="Select duration"
              value={when.duration}
              placeholder="30 min"
              options={DURATION_OPTIONS}
              // Lengthening the appointment can run it over the next booking,
              // which no list of start times can refuse in advance — see the
              // same handler on the reschedule dialog.
              onValueChange={(value) => {
                const next = { ...when, duration: value };
                set(
                  clashWith(next, day.booked)
                    ? { duration: value, time: "" }
                    : { duration: value }
                );
              }}
              disabled={busy}
            />
          </div>

          {/* Neither type asks a question here. Offline is told where it is —
              the practice's own address, which is the answer for every in-person
              appointment it will make — and Online is told nothing at all once an
              app is connected, since the link is made for it. While none is, that
              space carries the way to connect one. */}
          {type === "Online" ? (
            !anyConnected && <ConnectAppPrompt />
          ) : (
            <AppointmentAddressNote
              address={practiceAddress || null}
              // Always the practice's here: a new appointment has no address
              // of its own yet, and this dialog has nowhere to give it one.
              isDefault
              onLeave={onClose}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleAppointmentModal;
